import { PlusOutlined } from '@ant-design/icons';
import { Button, Form, GetProp, Image, Select, Upload } from 'antd';
import { UploadFile, UploadProps } from 'antd/es/upload';
import { getApi, postApi, putApi } from 'apis';
import AutoCompleteField from 'components/Global/AutoCompleteField';
import InputWithLabelAndSvg from 'components/Global/InputWithLabel';
import { FORMDATA, sanitizeString } from 'constants/Data';
import React, { FC, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { checkPermission, toastText } from 'utils/utils';
import styles from '../../Medicines/AddMedicineBody/index.module.scss';
import '../../Medicines/AddMedicineBody/index.scss';

type Category = {
	id: string;
	name: string;
	subCategories: { id: string; name: string; categoryId: string }[];
};

const resolveImageSrc = (key: string) => {
	if (!key) return '';
	if (key.startsWith('http://') || key.startsWith('https://')) return key;
	return `${process.env.REACT_APP_S3_BASE_URL}${key}`;
};

const AddEditComboBody: FC<any> = (props) => {
	const { addComboFields } = FORMDATA;
	const {
		closeDrawerByAnimation,
		editSelectedCombo,
		fetchCombos,
		setEditSelectedCombo,
	} = props;

	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [categoryList, setCategoryList] = useState<{ label: string; value: string }[]>([]);
	const [wholeCategories, setWholeCategories] = useState<Category[]>([]);
	const [selectedCategoryId, setSelectedCategoryId] = useState('');
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [primaryFileList, setPrimaryFileList] = useState<UploadFile[]>([]);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewImage, setPreviewImage] = useState('');

	const userProfile = useSelector((state: any) => state?.userProfile);
	type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];
	const isEditMode = Boolean(editSelectedCombo?.id);

	const isComboAddPermission = checkPermission(userProfile?.data?.permissions, {
		permissionName: 'ComboItems',
		permission: ['add'],
	});
	const isComboEditPermission = checkPermission(userProfile?.data?.permissions, {
		permissionName: 'ComboItems',
		permission: ['edit'],
	});
	const isImageAddPermission = checkPermission(userProfile?.data?.permissions, {
		permissionName: 'Images',
		permission: ['add'],
	});
	const isImageEditPermission = checkPermission(userProfile?.data?.permissions, {
		permissionName: 'Images',
		permission: ['edit'],
	});
	const canViewImages = checkPermission(userProfile?.data?.permissions, {
		permissionName: 'Images',
		permission: ['view'],
	});
	const canManageImages = isImageEditPermission || isImageAddPermission;
	const shouldDisableNonImageFields = isEditMode
		? !isComboEditPermission && isImageEditPermission
		: !isComboAddPermission;
	const canSubmit = isEditMode
		? isComboEditPermission || isImageEditPermission
		: isComboAddPermission;

	const subCategoryOptions = useMemo(() => {
		return (
			wholeCategories
				.find((item) => item.id === selectedCategoryId)
				?.subCategories?.map((sub) => ({
					label: sub.name,
					value: sub.id,
				})) || []
		);
	}, [wholeCategories, selectedCategoryId]);

	const fetchCategories = async () => {
		try {
			const res = await getApi('/combo-category/get-all');
			if (res?.data?.responseStatus !== 200 && !res?.data?.data) {
				throw new Error(res?.data?.message || 'Failed to fetch categories.');
			}
			const categories = res.data.data || [];
			setWholeCategories(categories);
			setCategoryList(
				categories.map((item: Category) => ({
					label: item.name,
					value: item.id,
				})),
			);
		} catch (error: any) {
			toastText(error?.response?.data?.message || error.message, 'error');
		}
	};

	useEffect(() => {
		fetchCategories();
	}, []);

	useEffect(() => {
		if (!isEditMode) {
			form.resetFields();
			setSelectedCategoryId('');
			setFileList([]);
			setPrimaryFileList([]);
			setPreviewOpen(false);
			setPreviewImage('');
			return;
		}

		const item = editSelectedCombo;
		setSelectedCategoryId(item.categoryId || '');
		form.setFieldsValue({
			...item,
			mrp: item?.mrp?.toString(),
			taxRate: item?.taxRate?.toString(),
		});

		const secondary = (item?.comboSecondaryImageUrl || []).map(
			(url: string, index: number) => ({
				uid: index.toString(),
				name: url,
				status: 'done' as const,
				url: resolveImageSrc(url),
			}),
		);
		setFileList(secondary);
		setPrimaryFileList(
			item?.comboPrimaryImageUrl
				? [
						{
							uid: '1',
							name: item.comboPrimaryImageUrl,
							status: 'done' as const,
							url: resolveImageSrc(item.comboPrimaryImageUrl),
						},
					]
				: [],
		);
	}, [editSelectedCombo, isEditMode, form]);

	const getDropdownOptions = (fieldId: string, roleOptions?: { label: string; value: string }[]) => {
		switch (fieldId) {
			case 'categoryId':
				return categoryList;
			case 'subCategoryId':
				return subCategoryOptions;
			default:
				return roleOptions || [];
		}
	};

	const onFinish = async (values: any) => {
		if (loading) return;
		if (isEditMode && !editSelectedCombo?.id) return;
		if (!isEditMode && !isComboAddPermission) return;

		const { primaryImage, secondaryImage, ...restData } = values;
		setLoading(true);
		const formData = new FormData();

		if (canManageImages) {
			let primaryImageUrl: File | null = null;
			let existingPrimaryImageUrl: string | null = null;

			if (primaryFileList?.length) {
				const file = primaryFileList[0];
				if ('originFileObj' in file && file.originFileObj instanceof File) {
					primaryImageUrl = file.originFileObj;
				} else if (file.url) {
					existingPrimaryImageUrl = file.name;
				}
			}

			if (primaryImageUrl) {
				formData.append('comboPrimaryImageUrl', primaryImageUrl);
			} else if (isEditMode && existingPrimaryImageUrl) {
				formData.append('existingComboPrimaryImageUrl', existingPrimaryImageUrl);
			}

			const existingSecondaryImageUrls: string[] = [];
			const newFiles: File[] = [];

			fileList?.forEach((file) => {
				if ('originFileObj' in file && file.originFileObj instanceof File) {
					newFiles.push(file.originFileObj);
				} else if (file.url) {
					existingSecondaryImageUrls.push(file.name);
				}
			});

			newFiles.forEach((file) => {
				formData.append('comboSecondaryImageUrl', file);
			});

			if (isEditMode) {
				formData.append(
					'existingComboSecondaryImageUrls',
					JSON.stringify(existingSecondaryImageUrls),
				);
			}
		} else if (isEditMode && editSelectedCombo) {
			if (editSelectedCombo.comboPrimaryImageUrl) {
				formData.append(
					'existingComboPrimaryImageUrl',
					editSelectedCombo.comboPrimaryImageUrl,
				);
			}
			if (editSelectedCombo.comboSecondaryImageUrl?.length) {
				formData.append(
					'existingComboSecondaryImageUrls',
					JSON.stringify(editSelectedCombo.comboSecondaryImageUrl),
				);
			}
		}

		Object.entries(restData).forEach(([key, value]) => {
			if (value === undefined || value === null) return;
			if (
				typeof value === 'object' &&
				value !== null &&
				'value' in value &&
				'label' in value
			) {
				formData.append(key, sanitizeString((value as { value: string }).value));
			} else if (typeof value === 'string') {
				formData.append(key, sanitizeString(value));
			} else {
				formData.append(key, String(value));
			}
		});

		try {
			const res = isEditMode
				? await putApi(`/combo/update/${editSelectedCombo.id}`, formData, true)
				: await postApi('/combo/create', formData, true);

			if (res?.data?.responseStatus === 200) {
				toast.success(res?.data?.message);
				fetchCombos?.();
				closeDrawerByAnimation();
			} else {
				throw new Error(res?.data?.message || 'Something went wrong.');
			}
		} catch (error: any) {
			toast.error(
				error?.response?.data?.message || error.message || 'Something went wrong.',
			);
		} finally {
			setLoading(false);
		}
	};

	const handleOnClick = () => {
		closeDrawerByAnimation();
		setEditSelectedCombo?.(null);
	};

	const getBase64 = (file: FileType): Promise<string> =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = (error) => reject(error);
		});

	const handlePreview = async (file: UploadFile) => {
		if (!canViewImages) {
			return;
		}

		if (!file.url && !file.preview) {
			file.preview = await getBase64(file.originFileObj as FileType);
		}

		setPreviewImage(file.url || (file.preview as string));
		setPreviewOpen(true);
	};

	const handleChange = (
		{ file }: { file: UploadFile; fileList: UploadFile[] },
	) => {
		if (file?.size) {
			const isLt1M = file.size / 1024 / 1024 < 1;
			if (!isLt1M) {
				toast.error('Image must be smaller than 1MB!');
			}
		}
	};

	const uploadButton = (
		<button style={{ border: 0, background: 'none' }} type="button">
			<PlusOutlined />
			<div style={{ marginTop: 8 }}>Upload</div>
		</button>
	);

	const propsUpload = {
		name: 'file',
		accept: '.jpg,.jpeg,.png,.webp',
		maxCount: 3,
		multiple: true,
		fileList,
		beforeUpload: () => false,
		listType: 'picture-card' as const,
		onPreview: canViewImages ? handlePreview : undefined,
		showUploadList: {
			showPreviewIcon: canViewImages,
			showRemoveIcon: true,
		},
	};

	const propsUploadPrimary = {
		name: 'file',
		accept: '.jpg,.jpeg,.png,.webp',
		maxCount: 1,
		fileList: primaryFileList,
		beforeUpload: () => false,
		listType: 'picture-card' as const,
		onPreview: canViewImages ? handlePreview : undefined,
		showUploadList: {
			showPreviewIcon: canViewImages,
			showRemoveIcon: true,
		},
	};

	return (
		<div className={styles['side-drawer-body']}>
			<Form
				form={form}
				name="comboForm"
				onFinish={onFinish}
				autoComplete="off"
				layout="vertical"
				labelAlign="left"
				className={styles['side-drawer-form']}
			>
				<div className={styles['side-drawer-form__inputs']}>
					{addComboFields.map((singleField: any, index: number) => {
						if (
							(singleField.type === 'primaryImage' ||
								singleField.type === 'secondaryImage') &&
							!canViewImages
						) {
							return null;
						}

						return (
							<React.Fragment key={index}>
								{singleField.type === 'text' && (
									<div
										className={
											shouldDisableNonImageFields
												? styles['side-drawer-form__disabled-field']
												: ''
										}
									>
										<AutoCompleteField
											form={form}
											singleUserInput={singleField}
											options={[]}
											onSearch={() => {}}
											disabled={shouldDisableNonImageFields}
										/>
									</div>
								)}

								{singleField.type === 'number' && (
									<div
										className={
											shouldDisableNonImageFields
												? styles['side-drawer-form__disabled-field']
												: ''
										}
									>
										<InputWithLabelAndSvg
											singleUserInput={singleField}
											disabled={shouldDisableNonImageFields}
										/>
									</div>
								)}

								{singleField.type === 'dropdown' && (
									<Form.Item
										key={singleField.id}
										name={singleField.name}
										label={singleField.title}
										rules={singleField.rules}
										className={`${styles['side-drawer-form__medicine--select']} ${
											shouldDisableNonImageFields
												? styles['side-drawer-form__medicine--select-disabled']
												: ''
										}`}
									>
										<Select
											placeholder={
												singleField.id === 'subCategoryId' && !selectedCategoryId
													? 'Select category first'
													: singleField.placeHolder
											}
											options={getDropdownOptions(
												singleField.id,
												singleField.roleOptions,
											)}
											showSearch
											optionFilterProp="label"
											style={{ height: '42px' }}
											disabled={shouldDisableNonImageFields}
											onChange={(val: string) => {
												if (singleField.id === 'categoryId') {
													setSelectedCategoryId(val || '');
													form.setFieldsValue({ subCategoryId: undefined });
												}
											}}
										/>
									</Form.Item>
								)}

								{singleField.type === 'primaryImage' && canManageImages && (
									<Form.Item label="Primary Image" name={singleField.name}>
										<Upload
											{...propsUploadPrimary}
											onChange={(info) => {
												handleChange(info);
												setPrimaryFileList(info.fileList);
											}}
										>
											{primaryFileList.length >= 1 ? null : uploadButton}
										</Upload>
									</Form.Item>
								)}

								{singleField.type === 'secondaryImage' && canManageImages && (
									<Form.Item label="Secondary Images" name={singleField.name}>
										<Upload
											{...propsUpload}
											onChange={(info) => {
												handleChange(info);
												setFileList(info.fileList);
											}}
										>
											{fileList.length >= 3 ? null : uploadButton}
										</Upload>
									</Form.Item>
								)}
							</React.Fragment>
						);
					})}
				</div>

				{previewImage && (
					<Image
						wrapperStyle={{ display: 'none' }}
						preview={{
							visible: previewOpen,
							onVisibleChange: (visible) => setPreviewOpen(visible),
							afterOpenChange: (visible) => !visible && setPreviewImage(''),
						}}
						src={previewImage}
					/>
				)}

				<div className={styles['side-drawer-form__buttons']}>
					<Button
						htmlType="submit"
						disabled={loading || !canSubmit}
						className={styles['side-drawer-form__buttons--edit']}
						loading={loading}
					>
						{isEditMode ? 'Update' : 'Save'}
					</Button>
					<Button
						htmlType="reset"
						className={styles['side-drawer-form__buttons--cancel']}
						onClick={handleOnClick}
					>
						Cancel
					</Button>
				</div>
			</Form>
		</div>
	);
};

export default AddEditComboBody;
